import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FaSearch, FaChevronDown, FaChevronUp, FaPlus, FaEdit, FaCheck, FaTimes } from 'react-icons/fa';
import Swal from 'sweetalert2';
import api from '../../../../utils/api';
import useColleges from '../../../../utils/useColleges';
import { useAuth } from '../../../../context/AuthContext';
import './Allocation.css';

const SearchDropdown = ({ placeholder, options, value, onChange, disabled }) => {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const selected = options.find(o => String(o.id) === String(value));
  const filtered = options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className={`student-allocation-for-transport-search-dropdown ${disabled ? 'disabled' : ''}`} ref={ref}>
      <div className="student-allocation-for-transport-search-dropdown-box" onClick={() => !disabled && setOpen(v => !v)}>
        <span style={{ color: selected ? '#1e293b' : '#94a3b8' }}>{selected ? selected.label : placeholder}</span>
        <FaChevronDown style={{ fontSize: '0.8rem', color: '#94a3b8', transition: '0.2s', transform: open ? 'rotate(180deg)' : 'none' }} />
      </div>
      {open && (
        <div className="student-allocation-for-transport-search-dropdown-list">
          <div className="student-allocation-for-transport-search-dropdown-input-wrapper">
            <FaSearch />
            <input type="text" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} autoFocus />
          </div>
          <ul>
            <li onClick={() => { onChange(''); setOpen(false); setSearch(''); }}>-- Clear --</li>
            {filtered.map(o => (
              <li 
                key={o.id} 
                className={`${String(o.id) === String(value) ? 'student-allocation-for-transport-selected-li' : ''} ${o.disabled ? 'disabled-option' : ''}`}
                onClick={() => { 
                  if (!o.disabled) { 
                    onChange(o.id); setOpen(false); setSearch(''); 
                  } else if (o.status === 'active') {
                    Swal.fire('Cannot Add', 'This student already has active transport in this session.', 'warning');
                  }
                }}
              >
                {o.label} {o.disabled && o.status === 'active' && <span className="student-allocation-for-transport-dropdown-full-badge">ACTIVE</span>}
              </li>
            ))}
            {filtered.length === 0 && <li className="empty-li">No results found</li>}
          </ul>
        </div>
      )}
    </div>
  );
};

// ── Reactivate Modal: inactive → active with new bus/route/stop/joinDate ──
const ReactivateModal = ({ alloc, busesData, routesData, onClose, onDone }) => {
  const [formBus, setFormBus]     = useState('');
  const [formRoute, setFormRoute] = useState('');
  const [formStop, setFormStop]   = useState('');
  const [formJoin, setFormJoin]   = useState('');
  const [price, setPrice]         = useState('');
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');

  // Initialize form fields properly
  useEffect(() => {
    if (alloc) {
      const busId = String(alloc.bus?._id || alloc.bus || '');
      const routeId = String(alloc.route?._id || alloc.route || '');
      const stop = alloc.stop || '';
      const p = alloc.price || '';
      
      // Set join date
      setFormJoin(new Date().toISOString().split('T')[0]);
      setFormStop(stop);
      setPrice(p);
      
      // If data is loaded, match IDs; otherwise set directly
      if (busesData.length > 0) {
        const matchingBus = busesData.find(b => String(b._id) === busId);
        setFormBus(matchingBus ? matchingBus._id : busId);
      } else {
        setFormBus(busId);
      }
      
      if (routesData.length > 0) {
        const matchingRoute = routesData.find(r => String(r._id) === routeId);
        setFormRoute(matchingRoute ? matchingRoute._id : routeId);
      } else {
        setFormRoute(routeId);
      }
    }
  }, [alloc, busesData, routesData]);

  const busOptions = busesData.map(b => ({ id: b._id, label: b.busNumber }));
  const routeOptions = routesData.map(r => ({ id: r._id, label: r.name }));
  const stopOptions = useMemo(() => {
    if (!formRoute) return [];
    const route = routesData.find(r => String(r._id) === String(formRoute));
    return (route?.stops || []).map(s => ({ id: s.name, label: `${s.name} — ₹${s.price}` }));
  }, [routesData, formRoute]);

  useEffect(() => {
    if (formRoute && formStop) {
      const route = routesData.find(r => String(r._id) === String(formRoute));
      const stop = route?.stops?.find(s => s.name === formStop);
      if (stop) setPrice(stop.price);
    }
  }, [formRoute, formStop, routesData]);

  const endDate = useMemo(() => {
    if (!formJoin) return '';
    const d = new Date(formJoin);
    d.setFullYear(d.getFullYear() + 1);
    d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
  }, [formJoin]);

  const handleConfirm = async () => {
    if (!formBus || !formRoute || !formStop || !formJoin) {
      setError('All fields are required'); return;
    }
    setSaving(true);
    try {
      await api.put(`/transport/allocations/${alloc._id}`, {
        bus: formBus, route: formRoute, stop: formStop,
        price, joinDate: formJoin, endDate, status: 'active', isActive: true,
      });
      onDone();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reactivate');
    } finally { setSaving(false); }
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.45)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:'16px'}} onClick={onClose}>
      <div style={{background:'#fff',borderRadius:'8px',width:'100%',maxWidth:'460px',boxShadow:'0 20px 60px rgba(0,0,0,0.2)',overflow:'hidden'}} onClick={e=>e.stopPropagation()}>
        <div style={{padding:'14px 20px',background:'#1e293b',color:'#fff',fontWeight:700,fontSize:'0.95rem',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <span>Activate Transport — {alloc.student?.name}</span>
          <button onClick={onClose} style={{background:'none',border:'none',color:'#94a3b8',cursor:'pointer',fontSize:'1.1rem',lineHeight:1}}>×</button>
        </div>
        <div style={{padding:'20px',display:'flex',flexDirection:'column',gap:'14px'}}>
          {[{label:'Bus',val:formBus,set:setFormBus,opts:busOptions},{label:'Route',val:formRoute,set:(v)=>{setFormRoute(v);setFormStop('');},opts:routeOptions},{label:'Stop',val:formStop,set:setFormStop,opts:stopOptions}].map(({label,val,set,opts})=>(
            <div key={label}>
              <div style={{fontSize:'0.82rem',fontWeight:700,color:'#374151',marginBottom:'5px'}}>{label} <span style={{color:'#ef4444'}}>*</span></div>
              <select value={val} onChange={e=>set(e.target.value)} style={{width:'100%',padding:'9px 12px',border:'1.5px solid #cbd5e1',borderRadius:'8px',fontSize:'0.9rem',outline:'none',fontFamily:'inherit'}}>
                <option value=''>-- Select {label} --</option>
                {opts.map(o=><option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </div>
          ))}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
            <div>
              <div style={{fontSize:'0.82rem',fontWeight:700,color:'#374151',marginBottom:'5px'}}>Join Date <span style={{color:'#ef4444'}}>*</span></div>
              <input type='date' value={formJoin} onChange={e=>setFormJoin(e.target.value)} style={{width:'100%',padding:'9px 12px',border:'1.5px solid #cbd5e1',borderRadius:'8px',fontSize:'0.9rem',outline:'none',fontFamily:'inherit',boxSizing:'border-box'}} />
            </div>
            <div>
              <div style={{fontSize:'0.82rem',fontWeight:700,color:'#374151',marginBottom:'5px'}}>End Date (auto)</div>
              <input type='date' value={endDate} readOnly style={{width:'100%',padding:'9px 12px',border:'1.5px solid #e2e8f0',borderRadius:'8px',fontSize:'0.9rem',background:'#f8fafc',color:'#64748b',fontFamily:'inherit',boxSizing:'border-box'}} />
            </div>
          </div>
          <div style={{background:'#f0fdf4',padding:'10px 14px',borderRadius:'8px',fontSize:'0.88rem',color:'#16a34a',fontWeight:700}}>Fees: ₹{price || '—'}</div>
          {error && <div style={{background:'#fef2f2',border:'1px solid #fecaca',borderRadius:'8px',padding:'10px 14px',color:'#dc2626',fontSize:'0.85rem',fontWeight:600}}>{error}</div>}
        </div>
        <div style={{display:'flex',justifyContent:'flex-end',gap:'10px',padding:'14px 20px',borderTop:'1px solid #e2e8f0',background:'#f8fafc'}}>
          <button onClick={onClose} disabled={saving} style={{padding:'9px 20px',border:'1px solid #cbd5e1',borderRadius:'8px',background:'#fff',color:'#475569',fontWeight:600,cursor:'pointer',fontSize:'0.88rem'}}>Cancel</button>
          <button onClick={handleConfirm} disabled={saving} style={{padding:'9px 20px',border:'none',borderRadius:'8px',background:'#10b981',color:'#fff',fontWeight:700,cursor:'pointer',fontSize:'0.88rem',opacity:saving?0.6:1}}>{saving?'Activating...':'Activate'}</button>
        </div>
      </div>
    </div>
  );
};

// ── Edit Modal: only bus, route, stop, price (no date changes) ──
const EditModal = ({ alloc, busesData, routesData, onClose, onDone }) => {
  const [formBus, setFormBus]     = useState('');
  const [formRoute, setFormRoute] = useState('');
  const [formStop, setFormStop]   = useState('');
  const [price, setPrice]         = useState('');
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');

  // Initialize form fields properly
  useEffect(() => {
    if (alloc) {
      const busId = String(alloc.bus?._id || alloc.bus || '');
      const routeId = String(alloc.route?._id || alloc.route || '');
      const stop = alloc.stop || '';
      const p = alloc.price || '';
      
      setFormStop(stop);
      setPrice(p);
      
      // If data is loaded, match IDs; otherwise set directly
      if (busesData.length > 0) {
        const matchingBus = busesData.find(b => String(b._id) === busId);
        setFormBus(matchingBus ? matchingBus._id : busId);
      } else {
        setFormBus(busId);
      }
      
      if (routesData.length > 0) {
        const matchingRoute = routesData.find(r => String(r._id) === routeId);
        setFormRoute(matchingRoute ? matchingRoute._id : routeId);
      } else {
        setFormRoute(routeId);
      }
    }
  }, [alloc, busesData, routesData]);

  const busOptions = busesData.map(b => ({ id: b._id, label: b.busNumber }));
  const routeOptions = routesData.map(r => ({ id: r._id, label: r.name }));
  const stopOptions = useMemo(() => {
    if (!formRoute) return [];
    const route = routesData.find(r => String(r._id) === String(formRoute));
    return (route?.stops || []).map(s => ({ id: s.name, label: `${s.name} — ₹${s.price}` }));
  }, [routesData, formRoute]);

  useEffect(() => {
    if (formRoute && formStop) {
      const route = routesData.find(r => String(r._id) === String(formRoute));
      const stop = route?.stops?.find(s => s.name === formStop);
      if (stop) setPrice(stop.price);
    }
  }, [formRoute, formStop, routesData]);

  // Check if any field has changed
  const hasChanges = () => {
    const origBus = String(alloc.bus?._id || alloc.bus || '');
    const origRoute = String(alloc.route?._id || alloc.route || '');
    const origStop = alloc.stop || '';
    const origPrice = String(alloc.price || '');
    
    return String(formBus) !== origBus || 
           String(formRoute) !== origRoute || 
           formStop !== origStop || 
           String(price) !== origPrice;
  };

  const handleConfirm = async () => {
    if (!formBus || !formRoute || !formStop) {
      setError('Bus, Route, and Stop are required'); 
      return;
    }
    
    // Only save if there are actual changes
    if (!hasChanges()) {
      onClose();
      return;
    }
    
    setSaving(true);
    try {
      await api.put(`/transport/allocations/${alloc._id}`, {
        bus: formBus, 
        route: formRoute, 
        stop: formStop,
        price: price,
      });
      onDone();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update');
    } finally { 
      setSaving(false); 
    }
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.45)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:'16px'}} onClick={onClose}>
      <div style={{background:'#fff',borderRadius:'8px',width:'100%',maxWidth:'460px',boxShadow:'0 20px 60px rgba(0,0,0,0.2)',overflow:'hidden'}} onClick={e=>e.stopPropagation()}>
        <div style={{padding:'14px 20px',background:'#1e293b',color:'#fff',fontWeight:700,fontSize:'0.95rem',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <span>Edit Transport — {alloc.student?.name}</span>
          <button onClick={onClose} style={{background:'none',border:'none',color:'#94a3b8',cursor:'pointer',fontSize:'1.1rem',lineHeight:1}}>×</button>
        </div>
        <div style={{padding:'20px',display:'flex',flexDirection:'column',gap:'14px'}}>
          {[{label:'Bus',val:formBus,set:setFormBus,opts:busOptions},{label:'Route',val:formRoute,set:(v)=>{setFormRoute(v);setFormStop('');},opts:routeOptions},{label:'Stop',val:formStop,set:setFormStop,opts:stopOptions}].map(({label,val,set,opts})=>(
            <div key={label}>
              <div style={{fontSize:'0.82rem',fontWeight:700,color:'#374151',marginBottom:'5px'}}>{label} <span style={{color:'#ef4444'}}>*</span></div>
              <select value={val} onChange={e=>set(e.target.value)} style={{width:'100%',padding:'9px 12px',border:'1.5px solid #cbd5e1',borderRadius:'8px',fontSize:'0.9rem',outline:'none',fontFamily:'inherit'}}>
                <option value=''>-- Select {label} --</option>
                {opts.map(o=><option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </div>
          ))}
          <div style={{background:'#f0fdf4',padding:'10px 14px',borderRadius:'8px',fontSize:'0.88rem',color:'#16a34a',fontWeight:700}}>Fees: ₹{price || '—'}</div>
          {error && <div style={{background:'#fef2f2',border:'1px solid #fecaca',borderRadius:'8px',padding:'10px 14px',color:'#dc2626',fontSize:'0.85rem',fontWeight:600}}>{error}</div>}
        </div>
        <div style={{display:'flex',justifyContent:'flex-end',gap:'10px',padding:'14px 20px',borderTop:'1px solid #e2e8f0',background:'#f8fafc'}}>
          <button onClick={onClose} disabled={saving} style={{padding:'9px 20px',border:'1px solid #cbd5e1',borderRadius:'8px',background:'#fff',color:'#475569',fontWeight:600,cursor:'pointer',fontSize:'0.88rem'}}>Cancel</button>
          <button 
            onClick={handleConfirm} 
            disabled={saving || !hasChanges()} 
            style={{padding:'9px 20px',border:'none',borderRadius:'8px',background:hasChanges() ? '#2563eb' : '#cbd5e1',color:'#fff',fontWeight:700,cursor:hasChanges() ? 'pointer' : 'not-allowed',fontSize:'0.88rem',opacity:saving?0.6:1}}
          >
            {saving?'Updating...':'Update'}
          </button>
        </div>
      </div>
    </div>
  );
};

const Allocation = () => {
  const { user }  = useAuth();
  const { colleges, loading: collegesLoading } = useColleges();
  const isAdmin   = user?.role === 'admin' || (user?.colleges?.length > 0);
  const isScoped  = user?.role === 'collegeadmin' || user?.role === 'clerk';

  const [allocations, setAllocations]     = useState([]);
  const [loading, setLoading]             = useState(false);
  const [saving, setSaving]               = useState(false);
  
  // Table Filters (Search Row)
  const [searchQuery, setSearchQuery]     = useState('');
  const [filterCollege, setFilterCollege] = useState('');
  const [filterSession, setFilterSession] = useState('');
  const [filterBatch, setFilterBatch]     = useState('');
  const [filterClass, setFilterClass]     = useState('');

  const [showAddForm, setShowAddForm]     = useState(false);
  const [editId, setEditId]               = useState(null);
  const [expandedRow, setExpandedRow]     = useState(null);
  const [filterStatus, setFilterStatus]   = useState(''); // default all — no toggle state needed
  const [reactivateModal, setReactivateModal] = useState(null);
  const [editModal, setEditModal]         = useState(null);
  const [formCollege, setFormCollege]   = useState('');
  const [formSession, setFormSession]   = useState('');
  const [formSessionName, setFormSessionName] = useState('');
  const [formStudent, setFormStudent]   = useState('');
  const [formBatch, setFormBatch]       = useState('');
  const [formClass, setFormClass]       = useState('');
  const [formBus, setFormBus]           = useState('');
  const [formRoute, setFormRoute]       = useState('');
  const [formStop, setFormStop]         = useState('');
  const [calculatedPrice, setCalculatedPrice] = useState('');
  
  // Join Date & End Date fields
  const [formJoinDate, setFormJoinDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [formEndDate, setFormEndDate]   = useState(() => {
    const d = new Date(); 
    d.setFullYear(d.getFullYear() + 1); 
    d.setDate(d.getDate() - 1); 
    return d.toISOString().split('T')[0];
  });

  // Data
  const [activeSession, setActiveSession] = useState(null);
  const [allStudents, setAllStudents]     = useState([]);
  const [busesData, setBusesData]         = useState([]);
  const [routesData, setRoutesData]       = useState([]);

  // Auto-set college for scoped/single
  useEffect(() => {
    if (!isAdmin && colleges.length > 0) setFormCollege(String(colleges[0]._id));
    else if (colleges.length === 1)      setFormCollege(String(colleges[0]._id));
  }, [colleges, isAdmin]);

  const loadAllocations = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/transport/allocations', { params: { all: 'true' } });
      setAllocations(data.data || []);
    } catch { setAllocations([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadAllocations(); }, []);

  // When college changes in form — fetch active session, students, buses, routes
  useEffect(() => {
    if (!formCollege) {
      setActiveSession(null); setAllStudents([]); setBusesData([]); setRoutesData([]);
      if (!editId) { setFormSession(''); setFormSessionName(''); setFormStudent(''); setFormBatch(''); setFormClass(''); }
      return;
    }
    api.get('/sessions', { params: { college: formCollege } })
      .then(r => {
        const active = (r.data.data || []).find(s => s.isActive);
        setActiveSession(active || null);
        // Only auto-set session for new allocations, not edits
        if (!editId) {
          if (active) { setFormSession(active._id); setFormSessionName(active.name); }
          else        { setFormSession(''); setFormSessionName(''); }
        }
      }).catch(() => { setActiveSession(null); if (!editId) { setFormSession(''); setFormSessionName(''); } });

    api.get('/students', { params: { college: formCollege, limit: 1000 } })
      .then(r => setAllStudents(r.data.data || [])).catch(() => setAllStudents([]));

    api.get('/transport/buses', { params: { college: formCollege } })
      .then(r => setBusesData(r.data.data || [])).catch(() => setBusesData([]));

    api.get('/transport/routes', { params: { activeOnly: 'true' } })
      .then(r => setRoutesData(r.data.data || [])).catch(() => setRoutesData([]));
  }, [formCollege]);

  // Auto-calculate End Date exactly 1 year minus 1 day from Join Date
  useEffect(() => {
    if (formJoinDate) {
      const date = new Date(formJoinDate);
      date.setFullYear(date.getFullYear() + 1);
      date.setDate(date.getDate() - 1);
      setFormEndDate(date.toISOString().split('T')[0]);
    } else {
      setFormEndDate('');
    }
  }, [formJoinDate]);

  // ── Helper to check if allocation is completed (old session after promotion) ──
  const isAllocationCompleted = (alloc) => {
    if (!alloc) return false;
    // Check if there's a newer allocation for same student
    const allocsForStudent = allocations.filter(a => 
      String(a.student?._id || a.student) === String(alloc.student?._id || alloc.student)
    );
    if (allocsForStudent.length <= 1) return false;
    
    // Sort by session name to find if this is older
    const currentSessionYear = parseInt((alloc.session?.name || '').match(/\d{4}/)?.[0] || 0);
    const hasNewer = allocsForStudent.some(a => {
      const year = parseInt((a.session?.name || '').match(/\d{4}/)?.[0] || 0);
      return year > currentSessionYear;
    });
    
    return hasNewer;
  };

  // When student selected — auto-fill batch and class (new allocation only)
  useEffect(() => {
    if (!formStudent || editId) return;
    const s = allStudents.find(x => x._id === formStudent);
    if (s) {
      if (s.admissionYear) setFormBatch(s.admissionYear);
      const clsId = s.class?._id || s.class || '';
      if (clsId) setFormClass(String(clsId));
    }
  }, [formStudent, allStudents, editId]);

  // Auto-calc price when stop changes
  useEffect(() => {
    if (formRoute && formStop) {
      const route = routesData.find(r => r._id === formRoute);
      const stop  = route?.stops?.find(s => s.name === formStop);
      setCalculatedPrice(stop?.price || '');
    } else {
      setCalculatedPrice('');
    }
  }, [formRoute, formStop, routesData]);

  // Students filtered by active session only
  const filteredStudents = useMemo(() => {
    if (!formSession) return allStudents;
    return allStudents.filter(s => String(s.session?._id || s.session) === formSession);
  }, [allStudents, formSession]);

  // Form Dropdown Options
  const batchOptions = useMemo(() => {
    const batches = [...new Set(filteredStudents.map(s => s.admissionYear).filter(Boolean))].sort();
    return batches.map(b => ({ id: b, label: `Batch ${b}` }));
  }, [filteredStudents]);

  const classOptions = useMemo(() => {
    const map = new Map();
    filteredStudents.forEach(s => {
      const id   = s.class?._id || s.class || '';
      const name = s.class?.name || s.class || '';
      const medium = s.medium || s.class?.medium || '';
      const label = medium ? `${name} (${medium})` : name;
      if (id && label) map.set(id, label);
    });
    return Array.from(map, ([id, label]) => ({ id, label }));
  }, [filteredStudents]);

  const displayedStudents = useMemo(() => {
    let list = filteredStudents;
    if (formBatch) list = list.filter(s => s.admissionYear === formBatch);
    if (formClass) list = list.filter(s => String(s.class?._id || s.class) === formClass || s.class?.name === formClass);
    return list;
  }, [filteredStudents, formBatch, formClass]);

  // Students already have allocation in current session
  const studentStatusMap = useMemo(() => {
    const map = new Map();
    allocations.forEach(a => {
      const studentId = String(a.student?._id || a.student);
      const sessionId = String(a.session?._id || a.session);
      if (String(a.session?._id || a.session) === formSession) {
        map.set(studentId, a.status || 'active'); // store status: active, inactive, completed, cancelled
      }
    });
    return map;
  }, [allocations, formSession]);

  const studentOptions = useMemo(() =>
    displayedStudents.map(s => {
      const studentId = String(s._id);
      const status = studentStatusMap.get(studentId);
      let label = `${s.name} (${s.admissionNo || 'N/A'})`;
      
      if (status === 'active') {
        label = `✓ ${label}`; // green
      } else if (status === 'inactive') {
        label = `⚠ ${label}`; // red/warning
      }
      
      return { 
        id: s._id, 
        label,
        status: status || 'none',
        disabled: status === 'active' // disable if already active
      };
    })
  , [displayedStudents, studentStatusMap]);
  
  
  // Calculate Full Buses
  const busOptions = useMemo(() => {
    return busesData.map(b => {
      const allocatedCount = allocations.filter(a => String(a.bus?._id || a.bus) === String(b._id)).length;
      const isFull = allocatedCount >= b.capacity;
      const isCurrentlyAssignedBus = editId && String(formBus) === String(b._id);
      return { 
        id: b._id, 
        label: `${b.busNumber} (${Math.max(0, b.capacity - allocatedCount)} seats left)`, 
        disabled: isFull && !isCurrentlyAssignedBus
      };
    });
  }, [busesData, allocations, editId, formBus]);

  const routeOptions = useMemo(() => routesData.map(r => ({ id: r._id, label: r.name })), [routesData]);

  // Stop options — label is stop name only (fee shown separately in Fees field)
  const stopOptions = useMemo(() => {
    const route = routesData.find(r => String(r._id) === String(formRoute));
    return (route?.stops || []).map(s => ({ id: s.name, label: s.name }));
  }, [routesData, formRoute]);

  // Auto-select route when bus is selected
  useEffect(() => {
    if (!formBus) return;
    const bus = busesData.find(b => String(b._id) === String(formBus));
    if (bus?.route) {
      const routeId = String(bus.route?._id || bus.route);
      if (formRoute !== routeId) {
        setFormRoute(routeId);
        setFormStop('');
      }
    }
  }, [formBus, busesData]);

  // Table Top Filters Dropdown Options (derived from existing allocations)
  const tableFilterOptions = useMemo(() => {
    let filtered = allocations;
    if (filterCollege) filtered = filtered.filter(a => String(a.college?._id || a.college) === filterCollege);
    return {
      sessions: [...new Map(filtered.map(a => [String(a.session?._id || a.session), a.session?.name || a.session]).filter(([id]) => id && id !== 'undefined')).entries()].map(([id, name]) => ({ id, name })),
      batches:  [...new Set(filtered.map(a => a.batch).filter(Boolean))].sort(),
      classes:  [...new Map(filtered.map(a => [String(a.class?._id || a.class), a.class?.name || a.class]).filter(([id]) => id && id !== 'undefined')).entries()].map(([id, name]) => ({ id, name }))
    };
  }, [allocations, filterCollege]);

  const resetForm = () => {
    const col = colleges.length === 1 ? colleges[0]._id : (isScoped ? formCollege : '');
    setFormCollege(col); setFormSession(''); setFormSessionName('');
    setFormStudent(''); setFormBatch(''); setFormClass('');
    setFormBus(''); setFormRoute(''); setFormStop(''); setCalculatedPrice('');
    setFormJoinDate(new Date().toISOString().split('T')[0]); // End Date auto updates
    setEditId(null); setShowAddForm(false);
  };

  const handleSubmit = async () => {
    if (!formCollege || !formStudent || !formBus || !formRoute || !formStop || !formJoinDate || !formEndDate) {
      Swal.fire('Missing Details', 'Please fill all required fields.', 'warning'); return;
    }
    if (!formSession) {
      Swal.fire('No Active Session', 'This college has no active session. Please create one first.', 'warning'); return;
    }
    setSaving(true);
    try {
      const selectedStudent = allStudents.find(x => x._id === formStudent);
      const studentClassId  = selectedStudent?.class?._id || selectedStudent?.class || formClass || undefined;

      const payload = {
        college: formCollege,
        session: formSession,          // ObjectId — always use _id, never the name string
        batch:   formBatch || selectedStudent?.admissionYear || '',
        class:   studentClassId,       // ObjectId
        student: formStudent,
        bus:     formBus,
        route:   formRoute,
        stop:    formStop,
        price:   calculatedPrice,
        joinDate: formJoinDate,
        endDate:  formEndDate,
      };
      if (editId) {
        await api.put(`/transport/allocations/${editId}`, payload);
        Swal.fire('Updated', 'Allocation updated', 'success');
      } else {
        await api.post('/transport/allocations', payload);
        Swal.fire('Allocated', 'Transport allocated successfully', 'success');
      }
      resetForm(); loadAllocations();
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to allocate', 'error');
    } finally { setSaving(false); }
  };

  const handleConfirm = async (alloc) => {
    const result = await Swal.fire({
      title: 'Confirm Transport',
      html: `Confirm transport for <b>${alloc.student?.name}</b>?<br/>Route: <b>${alloc.route?.name || '—'}</b> | Stop: <b>${alloc.stop || '—'}</b><br/>This will create the transport fee payment.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#10b981',
      confirmButtonText: 'Yes, Confirm'
    });
    if (!result.isConfirmed) return;
    try {
      await api.put(`/transport/allocations/${alloc._id}/confirm`);
      Swal.fire('Confirmed!', 'Transport allocation confirmed and fee created.', 'success');
      loadAllocations();
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to confirm', 'error');
    }
  };

  const handleDeactivate = async (alloc) => {
    const result = await Swal.fire({
      title: 'Deactivate Transport',
      html: `
        <p style="margin:0 0 12px;color:#374151">Are you sure you want to deactivate transport for <strong>${alloc.student?.name}</strong>?</p>
        <ul style="text-align:left;color:#6b7280;font-size:0.9rem;line-height:1.8;padding-left:20px;margin:0">
          <li>Transport will be <strong style="color:#dc2626">stopped</strong> for this session</li>
          <li>Pending fees will be <strong style="color:#dc2626">cancelled</strong></li>
          <li>Paid records will remain <strong style="color:#16a34a">unchanged</strong></li>
          <li>History will be <strong style="color:#16a34a">preserved</strong></li>
        </ul>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Deactivate',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    try {
      await api.delete(`/transport/allocations/${alloc._id}`);
      Swal.fire({ title: 'Deactivated', text: 'Transport stopped. History preserved.', icon: 'success', timer: 1800, showConfirmButton: false });
      loadAllocations();
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to deactivate', 'error');
    }
  };

  const handleReactivate = (alloc) => {
    setReactivateModal(alloc);
    // Load buses/routes if not already loaded
    if (busesData.length === 0 && alloc.college) {
      api.get('/transport/buses', { params: { college: alloc.college?._id || alloc.college } })
        .then(r => setBusesData(r.data.data || [])).catch(() => setBusesData([]));
    }
    if (routesData.length === 0) {
      api.get('/transport/routes', { params: { activeOnly: 'true' } })
        .then(r => setRoutesData(r.data.data || [])).catch(() => setRoutesData([]));
    }
  };

  const handleStudentSelect = (studentId) => {
    const status = studentStatusMap.get(String(studentId));
    
    if (status === 'active') {
      Swal.fire('Cannot Add', 'This student already has active transport in this session. You cannot add another allocation.', 'warning');
      return;
    }
    
    if (status === 'inactive') {
      const inactiveAlloc = allocations.find(a => 
        String(a.student?._id || a.student) === String(studentId) && 
        String(a.session?._id || a.session) === formSession &&
        a.status === 'inactive'
      );
      
      if (inactiveAlloc) {
        Swal.fire({
          title: 'Reactivate Transport?',
          text: `This student has an inactive allocation. Would you like to reactivate it instead of creating a new one?`,
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: '#10b981',
          confirmButtonText: 'Yes, Reactivate'
        }).then(result => {
          if (result.isConfirmed) {
            setReactivateModal(inactiveAlloc);
            setFormStudent('');
          }
        });
        return;
      }
    }
    
    setFormStudent(studentId);
  };

  const handleEdit = (alloc) => {
    setEditModal(alloc);
    // Load buses/routes if not already loaded
    if (busesData.length === 0 && alloc.college) {
      api.get('/transport/buses', { params: { college: alloc.college?._id || alloc.college } })
        .then(r => setBusesData(r.data.data || [])).catch(() => setBusesData([]));
    }
    if (routesData.length === 0) {
      api.get('/transport/routes', { params: { activeOnly: 'true' } })
        .then(r => setRoutesData(r.data.data || [])).catch(() => setRoutesData([]));
    }
  };

  const displayedAllocations = useMemo(() => {
    return allocations.filter(a => {
      const q = searchQuery.toLowerCase();
      const matchSearch  = (a.student?.name || '').toLowerCase().includes(q) || (a.bus?.busNumber || '').toLowerCase().includes(q);
      const matchCollege = filterCollege ? String(a.college?._id || a.college) === filterCollege : true;
      const matchSession = filterSession ? String(a.session?._id || a.session) === filterSession : true;
      const matchBatch   = filterBatch ? a.batch === filterBatch : true;
      const matchClass   = filterClass ? String(a.class?._id || a.class) === filterClass : true;
      // Status dropdown filter (all/active/inactive/completed/cancelled)
      const matchStatus  = filterStatus ? a.status === filterStatus : true;
      return matchSearch && matchCollege && matchSession && matchBatch && matchClass && matchStatus;
    });
  }, [allocations, searchQuery, filterCollege, filterSession, filterBatch, filterClass, filterStatus]);

  // Group by college for Table
  const grouped = useMemo(() => {
    const g = {};
    displayedAllocations.forEach(a => {
      const key = a.college?.name || colleges.find(c => String(c._id) === String(a.college?._id || a.college))?.name || 'Unassigned';
      if (!g[key]) g[key] = [];
      g[key].push(a);
    });
    return g;
  }, [displayedAllocations, colleges]);

  return (
    <div className="student-allocation-for-transport-container">

      {/* Reactivate Modal */}
      {reactivateModal && (
        <ReactivateModal
          alloc={reactivateModal}
          busesData={busesData}
          routesData={routesData}
          onClose={() => setReactivateModal(null)}
          onDone={() => { setReactivateModal(null); loadAllocations(); Swal.fire({ title: 'Activated', text: 'Transport is now active.', icon: 'success', timer: 1500, showConfirmButton: false }); }}
        />
      )}

      {/* Edit Modal */}
      {editModal && (
        <EditModal
          alloc={editModal}
          busesData={busesData}
          routesData={routesData}
          onClose={() => setEditModal(null)}
          onDone={() => { setEditModal(null); loadAllocations(); Swal.fire({ title: 'Updated', text: 'Transport details updated successfully.', icon: 'success', timer: 1500, showConfirmButton: false }); }}
        />
      )}

      {/* Table Filters & Controls */}
      <div className="student-allocation-for-transport-controls">
        <div className="student-allocation-for-transport-search-wrapper">
          <FaSearch className="student-allocation-for-transport-search-icon" />
          <input type="text" className="student-allocation-for-transport-search-input"
            placeholder="Search Student or Bus..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </div>

        {/* Status Dropdown — All by default */}
        <select
          className="student-allocation-for-transport-filter-select"
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>

        {!isScoped && colleges.length > 1 && (
          <select className="student-allocation-for-transport-filter-select" value={filterCollege} onChange={e => { setFilterCollege(e.target.value); setFilterSession(''); setFilterBatch(''); setFilterClass(''); }}>
            <option value="">All Colleges</option>
            {colleges.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
        )}

        <select className="student-allocation-for-transport-filter-select" value={filterSession} onChange={e => setFilterSession(e.target.value)}>
          <option value="">All Sessions</option>
          {tableFilterOptions.sessions.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>

        <select className="student-allocation-for-transport-filter-select" value={filterBatch} onChange={e => setFilterBatch(e.target.value)}>
          <option value="">All Batches</option>
          {tableFilterOptions.batches.map(b => <option key={b} value={b}>{b}</option>)}
        </select>

        <select className="student-allocation-for-transport-filter-select" value={filterClass} onChange={e => setFilterClass(e.target.value)}>
          <option value="">All Classes</option>
          {tableFilterOptions.classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>

        <button className="student-allocation-for-transport-btn-add" onClick={() => { if (showAddForm) resetForm(); else setShowAddForm(true); }}>
          <FaPlus /> {showAddForm ? 'Close Form' : 'Allocate Transport'}
        </button>
      </div>

      {/* Form */}
      {showAddForm && (
        <div className="student-allocation-for-transport-form">
          <h3 className="student-allocation-for-transport-form-title">{editId ? '✏️ Edit Allocation' : 'New Transport Allocation'}</h3>
          <div className="student-allocation-for-transport-form-grid">

            {isAdmin && (
              <div className="student-allocation-for-transport-form-group">
                <label>College <span style={{color:'#e74c3c'}}>*</span></label>
                <select value={formCollege} onChange={e => setFormCollege(e.target.value)} disabled={isScoped}>
                  <option value="">-- Select College --</option>
                  {colleges.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                </select>
              </div>
            )}

            <div className="student-allocation-for-transport-form-group">
              <label>Session (Current Active)</label>
              <input type="text"
                value={activeSession ? `${activeSession.name} ✓ Active` : (formCollege ? 'No active session' : 'Select college first')}
                readOnly disabled
                style={{ background: activeSession ? '#f0fdf4' : '#f8fafc', color: activeSession ? '#16a34a' : '#94a3b8', fontWeight: 600, cursor: 'not-allowed' }}
              />
            </div>

            <div className="student-allocation-for-transport-form-group">
              <label>Batch <span style={{fontSize:'0.72rem', color:'#64748b', fontWeight:400}}>filter or auto</span></label>
              <SearchDropdown placeholder="All Batches" options={batchOptions} value={formBatch}
                onChange={val => { setFormBatch(val); setFormStudent(''); }} disabled={!formCollege} />
            </div>

            <div className="student-allocation-for-transport-form-group">
              <label>Class <span style={{fontSize:'0.72rem', color:'#64748b', fontWeight:400}}>filter or auto</span></label>
              <SearchDropdown placeholder="All Classes" options={classOptions} value={formClass}
                onChange={val => { setFormClass(val); setFormStudent(''); }} disabled={!formCollege} />
            </div>

            <div className="student-allocation-for-transport-form-group">
              <label>Student <span style={{color:'#e74c3c'}}>*</span>
                <span style={{fontSize:'0.72rem', color:'#64748b', fontWeight:400, marginLeft:6}}>{displayedStudents.length} available</span>
              </label>
              <SearchDropdown placeholder="Select Student..." options={studentOptions} value={formStudent}
                onChange={handleStudentSelect} disabled={!formCollege} />
            </div>

            <div className="student-allocation-for-transport-form-group student-allocation-for-transport-divider">
              <label style={{color:'#3b82f6', fontWeight:700}}>🚌 Transport Details</label>
            </div>

            <div className="student-allocation-for-transport-form-group">
              <label>Bus <span style={{color:'#e74c3c'}}>*</span></label>
              <SearchDropdown placeholder="Select Bus..." options={busOptions} value={formBus}
                onChange={setFormBus} disabled={!formCollege} />
              {formBus && busOptions.find(b => b.id === formBus)?.disabled && (
                <span style={{fontSize:'0.75rem', color:'#e74c3c', marginTop:4, display:'block'}}>⚠ This bus is full. Please increase capacity or add a new bus.</span>
              )}
            </div>

            <div className="student-allocation-for-transport-form-group">
              <label>Route <span style={{color:'#e74c3c'}}>*</span> <span style={{fontSize:'0.72rem', color:'#64748b', fontWeight:400}}>(Locked to Bus)</span></label>
              <SearchDropdown placeholder="Select Route..." options={routeOptions} value={formRoute}
                onChange={val => { setFormRoute(val); setFormStop(''); }} disabled={!formCollege || !!formBus} />
            </div>

            <div className="student-allocation-for-transport-form-group">
              <label>Stop <span style={{color:'#e74c3c'}}>*</span></label>
              <SearchDropdown placeholder="Select Stop..." options={stopOptions} value={formStop}
                onChange={setFormStop} disabled={!formRoute} />
            </div>

            <div className="student-allocation-for-transport-form-group">
              <label>Fees (₹) <span style={{fontSize:'0.72rem', color:'#64748b', fontWeight:400}}>auto from stop</span></label>
              <input type="text" value={calculatedPrice ? `₹ ${calculatedPrice}` : '—'} readOnly
                style={{ background: '#f0fdf4', fontWeight: 700, color: '#10b981', cursor: 'not-allowed' }} />
            </div>

            <div className="student-allocation-for-transport-form-group">
              <label>Join Date <span style={{color:'#e74c3c'}}>*</span></label>
              <input type="date" value={formJoinDate} onChange={e => setFormJoinDate(e.target.value)} />
            </div>

            <div className="student-allocation-for-transport-form-group">
              <label>End Date <span style={{color:'#e74c3c'}}>*</span> <span style={{fontSize:'0.72rem', color:'#64748b', fontWeight:400}}>(Auto 1 Year)</span></label>
              <input type="date" value={formEndDate} disabled style={{ background: '#f8fafc', cursor: 'not-allowed', color: '#64748b', fontWeight: 600 }} />
            </div>

          </div>
          <div className="student-allocation-for-transport-form-buttons">
            <button className="student-allocation-for-transport-btn-cancel" onClick={resetForm} disabled={saving}>Cancel</button>
            <button className="student-allocation-for-transport-btn-confirm" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving...' : editId ? 'Update Allocation' : 'Allocate'}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{textAlign:'center', padding:'40px', color:'#64748b'}}>Loading allocations...</div>
      ) : Object.keys(grouped).length === 0 ? (
        <div style={{textAlign:'center', padding:'40px', color:'#64748b', background:'#fff', borderRadius:'8px'}}>No allocations found.</div>
      ) : (
        <div className="student-allocation-for-transport-grouped-container">
          {Object.entries(grouped).map(([collegeName, items]) => {
            // College header right side:
            // - If status dropdown = specific status: show that status count per session
            // - If status dropdown = All (empty): always show active count per session
            // - If session filter selected: show only that session's count
            const collegeAllocs = allocations.filter(a => {
              const cn = a.college?.name || colleges.find(c => String(c._id) === String(a.college?._id || a.college))?.name || 'Unassigned';
              return cn === collegeName;
            });
            const countStatus = filterStatus || 'active'; // always show active if no filter
            const countLabel  = filterStatus || 'active';
            let headerBadges;
            if (filterSession) {
              const cnt = collegeAllocs.filter(a =>
                String(a.session?._id || a.session) === filterSession && a.status === countStatus
              ).length;
              const sn = tableFilterOptions.sessions.find(s => s.id === filterSession)?.name || filterSession;
              headerBadges = [{ sn, cnt }];
            } else {
              const map = {};
              collegeAllocs.forEach(a => {
                if (a.status !== countStatus) return;
                const sn = a.session?.name || 'Unknown';
                map[sn] = (map[sn] || 0) + 1;
              });
              headerBadges = Object.entries(map).map(([sn, cnt]) => ({ sn, cnt }));
              if (headerBadges.length === 0) headerBadges = [{ sn: 'All Sessions', cnt: 0 }];
            }
            const badgeColor  = { active:'#16a34a', inactive:'#64748b', completed:'#7c3aed', cancelled:'#ef4444', pending_review:'#d97706' }[countStatus] || '#16a34a';
            const badgeBg     = { active:'#f0fdf4', inactive:'#f1f5f9', completed:'#f3e8ff', cancelled:'#fef2f2', pending_review:'#fef3c7' }[countStatus] || '#f0fdf4';
            const badgeBorder = { active:'#bbf7d0', inactive:'#e2e8f0', completed:'#e9d5ff', cancelled:'#fecaca', pending_review:'#fde68a' }[countStatus] || '#bbf7d0';
            return (
            <div key={collegeName} className="student-allocation-for-transport-table-group">
              <div className="student-allocation-for-transport-group-header">
                <span>🏫 {collegeName}</span>
                <div style={{display:'flex',gap:'8px',flexWrap:'wrap',alignItems:'center'}}>
                  {headerBadges.map(({sn, cnt}) => (
                    <span key={sn} style={{fontSize:'0.78rem',fontWeight:600,background:badgeBg,color:badgeColor,padding:'2px 10px',borderRadius:'8px',border:`1px solid ${badgeBorder}`}}>
                      {sn}: {cnt} {countLabel}
                    </span>
                  ))}
                </div>
              </div>
              <div className="student-allocation-for-transport-table-wrapper">
                <table className="student-allocation-for-transport-table">
                  <thead>
                    <tr>
                      <th>S.No</th>
                      <th style={{textAlign:'left'}}>Student</th>
                      <th className="student-allocation-for-transport-hide-sm">Session</th>
                      <th className="student-allocation-for-transport-hide-sm">Batch</th>
                      <th className="student-allocation-for-transport-hide-md">Class</th>
                      <th>Bus</th>
                      <th>Stop</th>
                      <th className="student-allocation-for-transport-hide-sm">Route</th>
                      <th>Fees</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((alloc, i) => {
                      const isCompleted = isAllocationCompleted(alloc);
                      // Edit allowed ONLY if allocation's session is the currently active session for that college
                      // i.e., the session stored in the allocation must match the active session
                      const allocSessionId = String(alloc.session?._id || alloc.session || '');
                      // Find active session for this college from allocations data
                      const activeAllocForCollege = allocations.find(a =>
                        String(a.college?._id || a.college) === String(alloc.college?._id || alloc.college) &&
                        a.status === 'active' &&
                        a.session?.isActive === true
                      );
                      // Fallback: use the session that appears most recently (highest year) among active allocs
                      const collegeActiveAllocs = allocations.filter(a =>
                        String(a.college?._id || a.college) === String(alloc.college?._id || alloc.college) &&
                        a.status === 'active'
                      );
                      const maxSessionYear = Math.max(...collegeActiveAllocs.map(a =>
                        parseInt((a.session?.name || '').match(/\d{4}/)?.[0] || 0)
                      ), 0);
                      const thisSessionYear = parseInt((alloc.session?.name || '').match(/\d{4}/)?.[0] || 0);
                      // canEdit = active status AND this session IS the current (highest year) active session
                      const isCurrentActiveSession = alloc.status === 'active' && thisSessionYear === maxSessionYear && maxSessionYear > 0;
                      const canEdit = isCurrentActiveSession;
                      
                      return (
                        <React.Fragment key={alloc._id}>
                          <tr className="student-allocation-for-transport-main-row" style={{ opacity: isCompleted ? 0.7 : 1 }}>
                            <td>{i + 1}</td>
                            <td style={{textAlign:'left'}}>
                              <div className="student-allocation-for-transport-name-wrapper"
                                onClick={() => setExpandedRow(expandedRow === alloc._id ? null : alloc._id)}>
                                <span style={{fontWeight:600}}>{alloc.student?.name || '—'}</span>
                                <button className="student-allocation-for-transport-expand-btn">
                                  {expandedRow === alloc._id ? <FaChevronUp /> : <FaChevronDown />}
                                </button>
                              </div>
                              <div style={{fontSize:'0.72rem', color:'#94a3b8'}}>{alloc.student?.admissionNo || ''}</div>
                            </td>
                            <td className="student-allocation-for-transport-hide-sm"><span className="student-allocation-for-transport-badge-session">{alloc.session?.name || '—'}</span></td>
                            <td className="student-allocation-for-transport-hide-sm">{alloc.batch || '—'}</td>
                            <td className="student-allocation-for-transport-hide-md">{alloc.class?.name || alloc.class || '—'}</td>
                            <td><span className="student-allocation-for-transport-bus-badge">{alloc.bus?.busNumber || '—'}</span></td>
                            <td style={{fontWeight:600, color:'#1e293b'}}>{alloc.stop || '—'}</td>
                            <td className="student-allocation-for-transport-hide-sm" style={{fontSize:'0.82rem', color:'#64748b'}}>{alloc.route?.name || '—'}</td>
                            <td><strong style={{color:'#10b981'}}>₹{alloc.price || 0}</strong></td>
                            <td>
                              {alloc.status === 'completed' ? (
                                <span style={{background:'#f3e8ff',color:'#7c3aed',padding:'4px 12px',borderRadius:'8px',fontSize:'0.75rem',fontWeight:700,border:'1px solid #e9d5ff',whiteSpace:'nowrap'}}>Completed</span>
                              ) : alloc.status === 'cancelled' ? (
                                <span style={{background:'#fef2f2',color:'#ef4444',padding:'4px 12px',borderRadius:'8px',fontSize:'0.75rem',fontWeight:700,border:'1px solid #fecaca',whiteSpace:'nowrap'}}>Cancelled</span>
                              ) : isCompleted ? (
                                <span style={{background:'#f3e8ff',color:'#7c3aed',padding:'4px 12px',borderRadius:'8px',fontSize:'0.75rem',fontWeight:700,border:'1px solid #e9d5ff',whiteSpace:'nowrap'}}>Completed</span>
                              ) : alloc.status === 'pending_review' ? (
                                <span style={{background:'#fef3c7',color:'#d97706',padding:'4px 12px',borderRadius:'8px',fontSize:'0.75rem',fontWeight:700,border:'1px solid #fde68a',whiteSpace:'nowrap'}}>Pending</span>
                              ) : (
                                /* Active / Inactive toggle — no text, only switch */
                                <div
                                  title={alloc.status === 'inactive' ? 'Click to Activate' : 'Active'}
                                  onClick={() => alloc.status === 'inactive' && handleReactivate(alloc)}
                                  style={{display:'inline-flex',alignItems:'center',cursor:alloc.status==='inactive'?'pointer':'default',userSelect:'none'}}
                                >
                                  <div style={{width:'38px',height:'20px',borderRadius:'10px',background:alloc.status==='active'?'#10b981':'#cbd5e1',position:'relative',transition:'background 0.2s',flexShrink:0}}>
                                    <div style={{position:'absolute',top:'2px',left:alloc.status==='active'?'20px':'2px',width:'16px',height:'16px',borderRadius:'50%',background:'#fff',transition:'left 0.2s',boxShadow:'0 1px 3px rgba(0,0,0,0.2)'}} />
                                  </div>
                                </div>
                              )}
                            </td>
                            <td>
                              <div style={{display:'flex',gap:'6px',flexWrap:'wrap',justifyContent:'center'}}>
                                {alloc.status === 'completed' || alloc.status === 'cancelled' ? (
                                  <span style={{fontSize:'0.75rem',color:'#94a3b8'}}>—</span>
                                ) : alloc.status === 'inactive' ? (
                                  <span style={{fontSize:'0.75rem',color:'#94a3b8'}}>—</span>
                                ) : alloc.status === 'pending_review' ? (
                                  <>
                                    <button className="student-allocation-for-transport-action-btn confirm"
                                      onClick={() => handleConfirm(alloc)}>Confirm</button>
                                    <button className="student-allocation-for-transport-btn-edit"
                                      onClick={() => handleEdit(alloc)}>Edit</button>
                                    <button className="student-allocation-for-transport-action-btn deactivate"
                                      onClick={() => handleDeactivate(alloc)}>Reject</button>
                                  </>
                                ) : canEdit ? (
                                  <button className="student-allocation-for-transport-btn-edit"
                                    onClick={() => handleEdit(alloc)}>Edit</button>
                                ) : alloc.status === 'active' ? (
                                  <button
                                    className="student-allocation-for-transport-btn-edit"
                                    style={{opacity:0.4,cursor:'not-allowed'}}
                                    onClick={() => Swal.fire({
                                      title: 'Cannot Edit',
                                      text: `This allocation is for session "${alloc.session?.name || '—'}" which is not the current active session. Only current session allocations can be edited.`,
                                      icon: 'warning',
                                      confirmButtonColor: '#3498db'
                                    })}
                                  >Edit</button>
                                ) : (
                                  <span style={{fontSize:'0.75rem',color:'#94a3b8'}}>—</span>
                                )}
                              </div>
                            </td>
                          </tr>
                          {/* Expand view renders ONLY hidden columns for mobile/tablet */}
                          {expandedRow === alloc._id && (
                            <tr className="student-allocation-for-transport-expanded-row">
                              <td colSpan="11">
                                <div className="student-allocation-for-transport-expanded-content">
                                  <div className="student-allocation-for-transport-exp-item student-allocation-for-transport-exp-sm-only"><span className="student-allocation-for-transport-exp-label">Session</span><span className="student-allocation-for-transport-exp-value">{alloc.session?.name || '—'}</span></div>
                                  <div className="student-allocation-for-transport-exp-item student-allocation-for-transport-exp-sm-only"><span className="student-allocation-for-transport-exp-label">Batch</span><span className="student-allocation-for-transport-exp-value">{alloc.batch || '—'}</span></div>
                                  <div className="student-allocation-for-transport-exp-item student-allocation-for-transport-exp-md-only"><span className="student-allocation-for-transport-exp-label">Class</span><span className="student-allocation-for-transport-exp-value">{alloc.class?.name || alloc.class || '—'}</span></div>
                                  <div className="student-allocation-for-transport-exp-item student-allocation-for-transport-exp-sm-only"><span className="student-allocation-for-transport-exp-label">Route</span><span className="student-allocation-for-transport-exp-value">{alloc.route?.name || '—'}</span></div>
                                  {alloc.cancelDate && <div className="student-allocation-for-transport-exp-item"><span className="student-allocation-for-transport-exp-label">Cancelled</span><span className="student-allocation-for-transport-exp-value">{new Date(alloc.cancelDate).toLocaleDateString()}</span></div>}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Allocation;